import React, { Component } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './iLocalAGGrid.css';
import { withTranslation } from 'react-i18next';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CustomLoadingCellRenderer from './customLoadingCellRenderer';
import { getDefaultColDef } from './defaultColDef';
import { isActionsColumn, RowActionsMenuHost } from './actionsMenu';
import { loadStoreLocationOptions } from './storeLocationFilter';
import { applyStoreLocationColumnFilters } from './StoreLocationDropdownFilter';
import { applyDateColumnFilters } from './filterUtils';

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 25];
export const ILOCAL_PAGE_SIZE_EXTRAS = [50, 100];
export const SELECTION_COL_ID = 'ilocal-selection';

export const isIlocalUser = () =>
  [true, 'true'].includes(
    typeof localStorage !== 'undefined' ? localStorage.getItem('isIlocalUser') : null
  );

/** Customer users: 10/20/25. iLocalBox users also get 50 and 100. */
export const getPageSizeOptions = (options) => {
  const base =
    Array.isArray(options) && options.length
      ? options
      : DEFAULT_PAGE_SIZE_OPTIONS;
  if (!isIlocalUser()) {
    return base;
  }
  const merged = [...base];
  ILOCAL_PAGE_SIZE_EXTRAS.forEach((size) => {
    if (!merged.includes(size)) {
      merged.push(size);
    }
  });
  return merged;
};

const SELECTION_COLUMN = {
  colId: SELECTION_COL_ID,
  headerName: '',
  valueGetter: () => '',
  checkboxSelection: true,
  headerCheckboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
  width: 48,
  minWidth: 48,
  maxWidth: 48,
  flex: 0,
  pinned: 'left',
  lockPinned: true,
  lockPosition: true,
  suppressMovable: true,
  sortable: false,
  filter: false,
  floatingFilter: false,
  resizable: false,
  suppressMenu: true,
  suppressFiltersToolPanel: true,
  suppressColumnsToolPanel: true,
  suppressAutoSize: true,
  menuTabs: [],
  cellClass: 'ilocal-selection-cell',
  headerClass: 'ilocal-selection-header',
};

const MAX_CONTENT_AUTOSIZE_WIDTH = 480;
const GRID_CHROME_PX = 112;
const PAGINATION_BAR_PX = 48;
const MIN_GRID_HEIGHT_PX = 220;

class iLocalAGGrid extends Component {
  constructor(props) {
    super(props);

    const initialPageSize =
      props.paginationPageSize != null
        ? props.paginationPageSize
        : DEFAULT_PAGE_SIZE;

    this.state = {
      loadingCellRenderer: CustomLoadingCellRenderer,
      loadingCellRendererParams: {
        loadingMessage: 'Please Wait...',
      },
      pageSize: initialPageSize,
      viewportCap: null,
    };
    this.gridApi = null;
    this.gridColumnApi = null;
    this.wrapperRef = React.createRef();
    this._stableColumnDefs = null;
    this._columnDefsSignature = null;
    this._stableDefaultColDef = null;
    this._selectedIds = new Set();
    this._selectedIdsSignature = '';
    this._selectionSyncTimer = null;
    this._userHasResizedColumns = false;
    this._autosizedOnce = false;
    this._autoSizeTimer = null;
    this._viewportTimer = null;
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState !== this.state) return true;
    // After SSRM is live, parent report setState (loader/data) rebuilds
    // columnDefs and overlay strings; feeding those into AgGridReact resets
    // the store and re-fetches forever. Ignore parent re-renders.
    if (this.gridApi && nextProps.rowModelType !== 'clientSide') {
      return false;
    }
    return true;
  }

  /**
   * Parent reports rebuild columnDefs[] / getDefaultColDef() every setState.
   * Feeding new references into AgGridReact resets SSRM and leaves Please Wait...
   * even after the network response arrives. Keep stable refs when the column
   * identity (colId/field + header) has not changed.
   */
  getStableColumnDefs = (columnDefs, rowSelection) => {
    const next = Array.isArray(columnDefs) ? columnDefs : [];
    const withSelection =
      rowSelection === 'multiple' ? this.withSelectionColumn(next) : next;
    const signature = withSelection
      .map((c) => `${c.colId || c.field || ''}:${c.headerName || ''}`)
      .join('|');
    if (signature && signature === this._columnDefsSignature && this._stableColumnDefs) {
      return this._stableColumnDefs;
    }
    this._columnDefsSignature = signature;
    this._stableColumnDefs = applyDateColumnFilters(
      applyStoreLocationColumnFilters(withSelection)
    );
    return this._stableColumnDefs;
  };

  withSelectionColumn = (columnDefs) => {
    const hasCheckbox = columnDefs.some(
      (col) =>
        col &&
        (col.colId === SELECTION_COL_ID ||
          col.checkboxSelection === true ||
          typeof col.checkboxSelection === 'function')
    );
    if (hasCheckbox) {
      return columnDefs;
    }
    return [SELECTION_COLUMN, ...columnDefs];
  };

  getStableDefaultColDef = (defaultColDef) => {
    if (defaultColDef) {
      if (this._stableDefaultColDef === defaultColDef) {
        return this._stableDefaultColDef;
      }
      // New object from getDefaultColDef() each render — reuse first resolved one.
      if (
        this._stableDefaultColDef &&
        defaultColDef.filter === this._stableDefaultColDef.filter &&
        defaultColDef.floatingFilter === this._stableDefaultColDef.floatingFilter
      ) {
        return this._stableDefaultColDef;
      }
      this._stableDefaultColDef = defaultColDef;
      return defaultColDef;
    }
    if (!this._stableDefaultColDef) {
      this._stableDefaultColDef = getDefaultColDef();
    }
    return this._stableDefaultColDef;
  };

  componentDidUpdate(prevProps) {
    // Ignore parent paginationPageSize churn after mount — reports often mutate
    // FilterObj.pageSize in didMount which would remount SSRM mid-fetch and
    // leave the grid stuck on Please Wait... Page size changes go through the
    // grid's own selector (handlePageSizeChange).
    if (
      !this.gridApi &&
      prevProps.paginationPageSize !== this.props.paginationPageSize &&
      this.props.paginationPageSize != null &&
      this.props.paginationPageSize !== this.state.pageSize
    ) {
      this.setState({ pageSize: this.props.paginationPageSize });
    }
  }

  getNoRowsMessage = () => {
    const { t, overlayNoRowsMessage } = this.props;
    if (overlayNoRowsMessage) {
      return overlayNoRowsMessage;
    }
    if (typeof t === 'function') {
      return t('Location.NoDataFound', {
        defaultValue: t('Assign.NoDataFound', { defaultValue: 'No Data Found' }),
      });
    }
    return 'No data found';
  };

  syncNoRowsOverlay = (api) => {
    if (!api || typeof api.getDisplayedRowCount !== 'function') {
      return;
    }
    const displayed = api.getDisplayedRowCount();
    if (displayed === 0) {
      api.showNoRowsOverlay();
    } else {
      api.hideOverlay();
    }
  };

  handleModelUpdated = (event) => {
    const api = event && event.api;
    const rowModelType =
      this.props.rowModelType != null ? this.props.rowModelType : 'serverSide';
    if (rowModelType !== 'serverSide') {
      this.syncNoRowsOverlay(api);
    }
    this.syncActiveFilterIndicators(api);
    this.scheduleSelectionSync(api);
    this.tryAutoSizeToContent(api, (event && event.columnApi) || this.gridColumnApi);
    if (typeof this.props.onModelUpdated === 'function') {
      this.props.onModelUpdated(event);
    }
  };

  handleFirstDataRendered = (event) => {
    const api = event && event.api;
    const rowModelType =
      this.props.rowModelType != null ? this.props.rowModelType : 'serverSide';
    if (rowModelType !== 'serverSide') {
      this.syncNoRowsOverlay(api);
    }
    this.syncActiveFilterIndicators(api);
    this.scheduleSelectionSync(api);
    this.tryAutoSizeToContent(api, (event && event.columnApi) || this.gridColumnApi);
    if (typeof this.props.onFirstDataRendered === 'function') {
      this.props.onFirstDataRendered(event);
    }
  };

  hasRenderableRowData = (api) => {
    if (!api || typeof api.getDisplayedRowCount !== 'function') {
      return false;
    }
    if (api.getDisplayedRowCount() <= 0) {
      return false;
    }
    const node =
      typeof api.getDisplayedRowAtIndex === 'function'
        ? api.getDisplayedRowAtIndex(0)
        : null;
    if (!node || node.stub || node.failedLoad) {
      return false;
    }
    return !!(node.data && typeof node.data === 'object');
  };

  getGridColumns = (columnApi, api) => {
    if (columnApi && typeof columnApi.getAllGridColumns === 'function') {
      return columnApi.getAllGridColumns() || [];
    }
    if (columnApi && typeof columnApi.getColumns === 'function') {
      return columnApi.getColumns() || [];
    }
    if (api && typeof api.getColumns === 'function') {
      return api.getColumns() || [];
    }
    if (api && typeof api.getAllGridColumns === 'function') {
      return api.getAllGridColumns() || [];
    }
    return [];
  };

  shouldSkipContentAutoSize = (column) => {
    if (!column) return true;
    const def = typeof column.getColDef === 'function' ? column.getColDef() : null;
    const id =
      (typeof column.getColId === 'function' && column.getColId()) ||
      (def && (def.colId || def.field)) ||
      '';
    if (!id || id === SELECTION_COL_ID) return true;
    if (def && def.suppressAutoSize) return true;
    if (def && def.flex) return true;
    if (
      def &&
      def.minWidth != null &&
      def.maxWidth != null &&
      def.minWidth === def.maxWidth
    ) {
      return true;
    }
    return false;
  };

  tryAutoSizeToContent = (api, columnApi) => {
    if (this.props.autoSizeToContent === false) return;
    if (this._userHasResizedColumns || this._autosizedOnce) return;
    if (!this.hasRenderableRowData(api)) return;
    this.scheduleAutoSizeToContent(api, columnApi);
  };

  scheduleAutoSizeToContent = (api, columnApi) => {
    if (this._autoSizeTimer) clearTimeout(this._autoSizeTimer);
    this._autoSizeTimer = setTimeout(() => {
      this.autoSizeColumnsToContent(api, columnApi || this.gridColumnApi);
    }, 50);
  };

  autoSizeColumnsToContent = (api, columnApi) => {
    if (this.props.autoSizeToContent === false) return;
    if (this._userHasResizedColumns || this._autosizedOnce) return;
    if (!this.hasRenderableRowData(api)) return;

    const colApi = columnApi || this.gridColumnApi;
    const columns = this.getGridColumns(colApi, api);
    const ids = columns
      .filter((column) => !this.shouldSkipContentAutoSize(column))
      .map((column) =>
        typeof column.getColId === 'function' ? column.getColId() : null
      )
      .filter(Boolean);

    if (!ids.length) {
      this._autosizedOnce = true;
      return;
    }

    const skipHeader = false;
    if (colApi && typeof colApi.autoSizeColumns === 'function') {
      colApi.autoSizeColumns(ids, skipHeader);
    } else if (api && typeof api.autoSizeColumns === 'function') {
      api.autoSizeColumns(ids, skipHeader);
    } else {
      return;
    }

    if (colApi && typeof colApi.applyColumnState === 'function') {
      const state =
        typeof colApi.getColumnState === 'function' ? colApi.getColumnState() : [];
      const clamped = (state || [])
        .filter(
          (col) =>
            col &&
            ids.indexOf(col.colId) !== -1 &&
            col.width > MAX_CONTENT_AUTOSIZE_WIDTH
        )
        .map((col) => ({ colId: col.colId, width: MAX_CONTENT_AUTOSIZE_WIDTH }));
      if (clamped.length) {
        colApi.applyColumnState({ state: clamped });
      }
    }

    this.fillRemainingGridWidth(api, colApi);
    this._autosizedOnce = true;
  };

  getAvailableGridWidth = () => {
    const root = this.wrapperRef && this.wrapperRef.current;
    if (!root) return 0;
    const gridRoot = root.querySelector('.ag-root-wrapper') || root;
    return gridRoot.clientWidth || 0;
  };

  getColumnsTotalWidth = (columns) =>
    (columns || []).reduce((sum, column) => {
      const width =
        typeof column.getActualWidth === 'function' ? column.getActualWidth() : 0;
      return sum + (width || 0);
    }, 0);

  fillRemainingGridWidth = (api, columnApi) => {
    if (!api || typeof api.sizeColumnsToFit !== 'function') return;
    const colApi = columnApi || this.gridColumnApi;
    const columns =
      colApi && typeof colApi.getAllDisplayedColumns === 'function'
        ? colApi.getAllDisplayedColumns() || []
        : this.getGridColumns(colApi, api);
    if (!columns.length) return;
    const available = this.getAvailableGridWidth();
    if (!available) return;
    const totalWidth = this.getColumnsTotalWidth(columns);
    // Only expand when content is narrower than the grid; never shrink
    // wide reports that should keep a horizontal scroll.
    if (totalWidth >= available - 2) return;
    api.sizeColumnsToFit();
  };

  handleGridSizeChanged = (event) => {
    if (
      this._autosizedOnce &&
      !this._userHasResizedColumns &&
      this.props.autoSizeToContent !== false
    ) {
      this.fillRemainingGridWidth(
        event && event.api,
        (event && event.columnApi) || this.gridColumnApi
      );
    }
    if (typeof this.props.onGridSizeChanged === 'function') {
      this.props.onGridSizeChanged(event);
    }
  };

  handleColumnResized = (event) => {
    if (event && event.finished && event.source === 'uiColumnResized') {
      this._userHasResizedColumns = true;
    }
    if (typeof this.props.onColumnResized === 'function') {
      this.props.onColumnResized(event);
    }
  };

  /**
   * Highlight header + floating-filter cells for columns with an active filter.
   * AG Grid 29 does not expose api.getColumns(); use getFilterModel() + input values.
   */
  isFilterModelActive = (model) => {
    if (!model || typeof model !== 'object') return false;
    if (model.filter != null && String(model.filter).trim() !== '') return true;
    if (model.filterTo != null && String(model.filterTo).trim() !== '') return true;
    if (model.dateFrom || model.dateTo) return true;
    if (Array.isArray(model.values) && model.values.length > 0) return true;
    if (Array.isArray(model.filterModels)) {
      return model.filterModels.some((c) => this.isFilterModelActive(c));
    }
    if (Array.isArray(model.conditions)) {
      return model.conditions.some((c) => this.isFilterModelActive(c));
    }
    if (model.condition1 && this.isFilterModelActive(model.condition1)) return true;
    if (model.condition2 && this.isFilterModelActive(model.condition2)) return true;
    return false;
  };

  syncActiveFilterIndicators = (api) => {
    const root = this.wrapperRef && this.wrapperRef.current;
    if (!root) return;

    const filterModel =
      api && typeof api.getFilterModel === 'function'
        ? api.getFilterModel() || {}
        : {};

    const activeIds = new Set();
    Object.keys(filterModel).forEach((colId) => {
      if (this.isFilterModelActive(filterModel[colId])) {
        activeIds.add(colId);
      }
    });

    // Floating filter row: also treat non-empty inputs as active (while typing /
    // before filterChanged fires).
    root.querySelectorAll('.ag-header-cell.ag-floating-filter').forEach((cell) => {
      const colId = cell.getAttribute('col-id');
      const input = cell.querySelector('input');
      const hasInputValue = !!(input && String(input.value || '').trim());
      const active = hasInputValue || (colId != null && activeIds.has(colId));
      cell.classList.toggle('ilocal-floating-filter-active', active);
      if (active && colId != null) {
        activeIds.add(colId);
      }
    });

    // Header label row
    root.querySelectorAll('.ag-header-cell:not(.ag-floating-filter)').forEach((cell) => {
      const colId = cell.getAttribute('col-id');
      const active = colId != null && activeIds.has(colId);
      cell.classList.toggle('ilocal-header-filtered', active);
    });
  };

  bindFilterInputListener = () => {
    const root = this.wrapperRef && this.wrapperRef.current;
    if (!root || this._filterInputBound) return;
    this._filterInputBound = true;
    this._onFilterInput = () => {
      this.syncActiveFilterIndicators(this.gridApi);
    };
    // Capture typing in floating filters immediately.
    root.addEventListener('input', this._onFilterInput, true);
    root.addEventListener('change', this._onFilterInput, true);
  };

  handleFilterChanged = (event) => {
    const api = event && event.api;
    this.syncActiveFilterIndicators(api);
    if (typeof this.props.onFilterChanged === 'function') {
      this.props.onFilterChanged(event);
    }
  };

  handleFilterModified = (event) => {
    this.syncActiveFilterIndicators(event && event.api);
    if (typeof this.props.onFilterModified === 'function') {
      this.props.onFilterModified(event);
    }
  };

  /**
   * Loading placeholders have no data yet. If getRowId changes from `ssrm-0`
   * (loading) to a business key (loaded), AG Grid 29 SSRM keeps Please Wait...
   * and retries getRows forever. Always key by rowIndex when it exists.
   */
  resolveGetRowId = (params) => {
    const idx =
      params && params.node && params.node.rowIndex != null && params.node.rowIndex >= 0
        ? params.node.rowIndex
        : params && params.rowIndex != null && params.rowIndex >= 0
          ? params.rowIndex
          : null;
    if (idx != null) {
      return `ssrm-${idx}`;
    }
    const d = params && params.data;
    if (d && d.__ssrmId != null && String(d.__ssrmId) !== '') {
      return String(d.__ssrmId);
    }
    const source = this.props.getRowId;
    if (typeof source === 'function' && d) {
      const id = source(params);
      if (id != null && String(id).trim() !== '' && String(id) !== 'undefined') {
        return String(id);
      }
    }
    return 'ssrm-pending';
  };

  handleGridReady = (params) => {
    const sameApi = this.gridApi === params.api;
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi || null;
    this.bindFilterInputListener();
    this.syncActiveFilterIndicators(params.api);
    loadStoreLocationOptions();
    this._selectedIds = new Set();
    this._selectedIdsSignature = '';
    this.syncViewportCap();
    // Same api instance already has a datasource — do not attach again.
    if (sameApi) return;
    if (typeof this.props.onGridReady === 'function') {
      this.props.onGridReady(params);
    }
  };

  componentDidMount() {
    this.syncViewportCap();
    if (typeof window !== 'undefined') {
      this._onWindowResize = () => {
        if (this._viewportTimer) clearTimeout(this._viewportTimer);
        this._viewportTimer = setTimeout(() => {
          this._viewportTimer = null;
          this.syncViewportCap();
        }, 100);
      };
      window.addEventListener('resize', this._onWindowResize);
    }
  }

  componentWillUnmount() {
    const root = this.wrapperRef && this.wrapperRef.current;
    if (root && this._onFilterInput) {
      root.removeEventListener('input', this._onFilterInput, true);
      root.removeEventListener('change', this._onFilterInput, true);
    }
    if (this._selectionSyncTimer) clearTimeout(this._selectionSyncTimer);
    if (this._autoSizeTimer) clearTimeout(this._autoSizeTimer);
    if (this._viewportTimer) clearTimeout(this._viewportTimer);
    if (this._onWindowResize && typeof window !== 'undefined') {
      window.removeEventListener('resize', this._onWindowResize);
    }
  }

  // Prevent ⋮ Actions clicks from bubbling into row/cell selection.
  handleCellMouseDown = (event) => {
    if (isActionsColumn(event.column) && event.event) {
      event.event.stopPropagation();
      if (typeof event.event.stopImmediatePropagation === 'function') {
        event.event.stopImmediatePropagation();
      }
    }
    if (typeof this.props.onCellMouseDown === 'function') {
      this.props.onCellMouseDown(event);
    }
  };

  handleCellClicked = (event) => {
    if (typeof this.props.onCellClicked === 'function') {
      this.props.onCellClicked(event);
    }
  };

  getSelectableRowId = (node) => {
    const data = node && node.data;
    if (!data) return null;
    if (data.id != null && data.id !== '') return data.id;
    if (data.ID != null && data.ID !== '') return data.ID;
    return null;
  };

  syncAccumulatedSelection = (api, { removeUnselectedLoaded = false } = {}) => {
    if (!api || typeof api.forEachNode !== 'function') return;
    api.forEachNode((node) => {
      const id = this.getSelectableRowId(node);
      if (id == null) return;
      if (node.isSelected()) {
        this._selectedIds.add(id);
      } else if (removeUnselectedLoaded) {
        this._selectedIds.delete(id);
      }
    });
  };

  scheduleSelectionSync = (api) => {
    if (!api) return;
    if (this._selectionSyncTimer) clearTimeout(this._selectionSyncTimer);
    this._selectionSyncTimer = setTimeout(() => {
      this.syncAccumulatedSelection(api, { removeUnselectedLoaded: false });
      this.emitSelectionToParent({ api });
    }, 0);
  };

  emitSelectionToParent = (event) => {
    const api = (event && event.api) || this.gridApi;
    if (!api) return;
    if (
      typeof this.props.onRowSelected !== 'function' &&
      typeof this.props.onSelectionChanged !== 'function'
    ) {
      return;
    }

    const selectedNodes = [...this._selectedIds].map((id) => ({
      data: { id, ID: id },
      isSelected: () => true,
    }));
    const signature = selectedNodes.map((n) => n.data.id).join('|');
    if (signature === this._selectedIdsSignature) return;
    this._selectedIdsSignature = signature;

    const forwarded = {
      ...(event || {}),
      api: {
        getSelectedNodes: () => selectedNodes,
      },
    };
    if (typeof this.props.onRowSelected === 'function') {
      this.props.onRowSelected(forwarded);
    }
    if (typeof this.props.onSelectionChanged === 'function') {
      this.props.onSelectionChanged(forwarded);
    }
  };

  // Checkbox / header "select all" updates come through selectionChanged.
  // List pages listen to onRowSelected, so forward both from one place.
  handleSelectionChanged = (event) => {
    const source = event && event.source;
    const isUserSelection =
      source === 'checkboxSelected' ||
      source === 'uiSelectAll' ||
      source === 'uiSelectAllFiltered' ||
      source === 'uiSelectAllCurrentPage';
    this.syncAccumulatedSelection(event && event.api, {
      removeUnselectedLoaded: isUserSelection,
    });
    this.emitSelectionToParent(event);
  };

  getMaxViewportHeight = () => {
    if (typeof window === 'undefined') {
      return GRID_CHROME_PX + DEFAULT_PAGE_SIZE * 45 + PAGINATION_BAR_PX;
    }
    const root = this.wrapperRef && this.wrapperRef.current;
    const top = root ? root.getBoundingClientRect().top : 160;
    return Math.max(
      MIN_GRID_HEIGHT_PX,
      Math.floor(window.innerHeight - Math.max(0, top) - 8)
    );
  };

  syncViewportCap = () => {
    const cap = this.getMaxViewportHeight();
    if (this.state.viewportCap === cap) return;
    this.setState({ viewportCap: cap });
  };

  getResolvedGridHeight = (pageSize, rowHeight, pagination, gridHeight) => {
    if (gridHeight != null) return gridHeight;
    const rows = pageSize || DEFAULT_PAGE_SIZE;
    const content =
      GRID_CHROME_PX +
      rows * (rowHeight || 40) +
      (pagination ? PAGINATION_BAR_PX : 0);
    const cap = this.state.viewportCap || this.getMaxViewportHeight();
    return Math.min(content, cap);
  };

  handlePaginationChanged = (event) => {
    this.scheduleSelectionSync(event && event.api);
    if (typeof this.props.onPaginationChanged === 'function') {
      this.props.onPaginationChanged(event);
    }
  };

  refreshAfterPageSizeChange = (api) => {
    if (!api) return;
    if (typeof api.refreshServerSideStore === 'function') {
      api.refreshServerSideStore({ purge: true });
    } else if (typeof api.refreshServerSide === 'function') {
      api.refreshServerSide({ purge: true });
    }
  };

  handlePageSizeChange = (event) => {
    const nextSize = Number(event.target.value);
    if (!nextSize || nextSize === this.state.pageSize) {
      return;
    }
    const allowed = getPageSizeOptions(this.props.paginationPageSizeSelector);
    if (!allowed.includes(nextSize)) {
      return;
    }

    this.setState({ pageSize: nextSize }, () => {
      const api = this.gridApi;
      if (!api) return;

      if (typeof api.paginationSetPageSize === 'function') {
        api.paginationSetPageSize(nextSize);
      }
      if (typeof api.setCacheBlockSize === 'function') {
        api.setCacheBlockSize(nextSize);
      }
      if (typeof api.paginationGoToFirstPage === 'function') {
        api.paginationGoToFirstPage();
      }

      const rowModelType =
        this.props.rowModelType != null ? this.props.rowModelType : 'serverSide';
      if (rowModelType === 'serverSide') {
        this.refreshAfterPageSizeChange(api);
      }

      if (typeof this.props.onPaginationChanged === 'function') {
        this.props.onPaginationChanged({ api });
      }
    });
  };

  render() {
    const {
      gridHeight,
      columnDefs,
      rowData,
      sideBar = false,
      suppressMenuHide = true,
      rowModelType = 'serverSide',
      enableSorting = true,
      enableFilter = true,
      floatingFilter = true,
      enableColResize = true,
      rowSelection,
      defaultColDef,
      cacheBlockSize,
      maxBlocksInCache,
      rowBuffer,
      pagination = true,
      paginationPageSizeSelector = DEFAULT_PAGE_SIZE_OPTIONS,
      domLayout = 'autoHeight',
      rowHeight = 45,
      getContextMenuItems,
      allowContextMenuWithControlKey = true,
      overlayNoRowsTemplate,
      className,
      style,
      t,
      autoSizeToContent = true,
      suppressColumnVirtualisation,
    } = this.props;
    const { loadingCellRendererParams, loadingCellRenderer, pageSize } = this.state;

    const resolvedDefaultColDef = this.getStableDefaultColDef(defaultColDef);
    const resolvedColumnDefs = this.getStableColumnDefs(columnDefs, rowSelection);
    const noRowsMessage = this.getNoRowsMessage();
    if (this._localeNoRows !== noRowsMessage) {
      this._localeNoRows = noRowsMessage;
      this._localeText = { noRowsToShow: noRowsMessage };
    }
    const resolvedNoRowsTemplate =
      overlayNoRowsTemplate ||
      `<span class="ag-overlay-no-rows-center" style="padding: 24px; color: rgba(0,0,0,0.54); font-size: 14px;">${String(
        noRowsMessage
      )
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')}</span>`;

    const pageSizeOptions = getPageSizeOptions(paginationPageSizeSelector);

    const resolvedCacheBlockSize =
      cacheBlockSize != null ? cacheBlockSize : pageSize;

    const isServerSide = rowModelType === 'serverSide';
    const resolvedDomLayout = isServerSide ? 'normal' : domLayout;
    const ssrmHeight = this.getResolvedGridHeight(
      pageSize,
      rowHeight,
      pagination,
      gridHeight
    );

    if (!this._overlayTemplate || this._overlayMessage !== noRowsMessage) {
      this._overlayMessage = noRowsMessage;
      this._overlayTemplate =
        overlayNoRowsTemplate ||
        `<span class="ag-overlay-no-rows-center" style="padding: 24px; color: rgba(0,0,0,0.54); font-size: 14px;">${String(
          noRowsMessage
        )
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')}</span>`;
    }

    const wrapperStyle = {
      boxSizing: 'border-box',
      width: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      ...(isServerSide || gridHeight != null
        ? { height: ssrmHeight, paddingBottom: pagination ? 40 : 0 }
        : { minHeight: 180 }),
      ...(style || {}),
    };

    const pageSizeLabel =
      (typeof t === 'function' &&
        t('Global.Label.PageSize', { defaultValue: 'Page Size' })) ||
      'Page Size';

    // maxBlocksInCache: 0 purges every loaded block (esp. with autoHeight).
    // Only pass a positive limit for client-side; SSRM defaults below.
    //
    // SSRM + pagination: default maxBlocksInCache=1. Unlimited cache keeps
    // early blocks after Last-page jumps; Previous then walks startRow
    // 10,20,30… Do NOT purgeServerSideStore on newPage — that collapses
    // rowCount to the loading placeholder and snaps the pager to page 1.
    // Pair with createServerSideDatasource off-target block guard.
    const gridProps = {
      rowBuffer,
      rowModelType,
      cacheBlockSize: resolvedCacheBlockSize,
      columnDefs: resolvedColumnDefs,
      rowData,
      sideBar,
      suppressMenuHide,
      onGridReady: this.handleGridReady,
      loadingCellRenderer,
      loadingCellRendererParams,
      enableSorting,
      enableFilter,
      floatingFilter,
      enableColResize,
      rowSelection,
      suppressRowClickSelection: rowSelection === 'multiple',
      rowMultiSelectWithClick: false,
      defaultColDef: resolvedDefaultColDef,
      onRowSelected: this.handleSelectionChanged,
      onSelectionChanged: this.handleSelectionChanged,
      onCellMouseDown: this.handleCellMouseDown,
      onCellClicked: this.handleCellClicked,
      onRowClicked: this.props.onRowClicked,
      pagination,
      paginationPageSize: pageSize,
      onPaginationChanged: this.handlePaginationChanged,
      domLayout: resolvedDomLayout,
      rowHeight,
      getRowId: this.resolveGetRowId,
      onFirstDataRendered: this.handleFirstDataRendered,
      onModelUpdated: this.handleModelUpdated,
      onColumnResized: this.handleColumnResized,
      onGridSizeChanged: this.handleGridSizeChanged,
      onFilterChanged: this.handleFilterChanged,
      suppressColumnVirtualisation:
        suppressColumnVirtualisation != null
          ? suppressColumnVirtualisation
          : autoSizeToContent,
      onFilterModified: this.handleFilterModified,
      getContextMenuItems,
      allowContextMenuWithControlKey,
      overlayNoRowsTemplate: this._overlayTemplate || resolvedNoRowsTemplate,
      localeText: this._localeText,
    };
    if (isServerSide) {
      gridProps.domLayout = resolvedDomLayout;
      gridProps.maxConcurrentDatasourceRequests = 1;
      if (rowBuffer == null) {
        gridProps.rowBuffer = 0;
      }
      if (maxBlocksInCache != null && maxBlocksInCache > 0) {
        gridProps.maxBlocksInCache = maxBlocksInCache;
      } else {
        gridProps.maxBlocksInCache = 1;
      }
    } else if (maxBlocksInCache != null && maxBlocksInCache > 0) {
      gridProps.maxBlocksInCache = maxBlocksInCache;
    }

    return (
      <div
        ref={this.wrapperRef}
        className={className || 'ag-theme-alpine'}
        style={wrapperStyle}
      >
        <div className="ilocal-ag-grid-body">
          <AgGridReact {...gridProps} />
        </div>
        {pagination ? (
          <div
            className="ilocal-ag-page-size"
            style={{
              position: 'absolute',
              left: 8,
              bottom: 6,
              zIndex: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              fontSize: 13,
              color: '#181d1f',
              background: 'transparent',
            }}
          >
            <label htmlFor="ilocal-ag-page-size-select" style={{ margin: 0 }}>
              {pageSizeLabel}
            </label>
            <Select
              id="ilocal-ag-page-size-select"
              value={pageSize}
              onChange={this.handlePageSizeChange}
              variant="standard"
              disableUnderline
              inputProps={{ 'aria-label': pageSizeLabel }}
              MenuProps={{
                disableScrollLock: true,
                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                transformOrigin: { vertical: 'bottom', horizontal: 'left' },
              }}
              sx={{
                fontSize: 13,
                height: 26,
                minWidth: 56,
                '& .MuiSelect-select': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: '8px',
                  paddingRight: '24px !important',
                  boxSizing: 'border-box',
                  border: '1px solid #babfc7',
                  borderRadius: '3px',
                  backgroundColor: '#fff',
                  height: 26,
                  lineHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size} dense>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </div>
        ) : null}
        <RowActionsMenuHost />
      </div>
    );
  }
}

export default withTranslation()(iLocalAGGrid);

export { getDefaultColDef } from './defaultColDef';
export {
  withStoreLocationColumnFilter,
  applyStoreLocationColumnFilters,
} from './StoreLocationDropdownFilter';
export { createServerSideDatasource } from './createServerSideDatasource';
export {
  buildSearchObject,
  buildManageBoxesSearchObject,
  buildManageAccountsSearchObject,
  foldFiltersToSearchText,
  formatAddressField,
  applySortModel,
  DATE_COLUMN_FILTER_PARAMS,
  withDateColumnFilter,
  applyDateColumnFilters,
  CONSULT_STATUS_VALUES,
  consultStatusFilterParams,
  buildDateRangeFilterModel,
  seedReportDateFilter,
  attachReportServerSideDatasource,
  applyDateRangeFromFilterModel,
  restoreDefaultReportDates,
  applyReportFilterModel,
  normalizeColumnFilter,
  serializeFilterModelEntry,
} from './filterUtils';
export { DateRangeFilter, DateRangeFloatingFilter } from './DateRangeFilter';
export {
  PHARMACY_REPORT_SORT_MAP,
  AUDIT_REPORT_SORT_MAP,
  PATIENT_LIST_SORT_MAP,
  RETAIL_REPORT_SORT_MAP,
  searchFieldMapFromSortMap,
} from './reportColumnMaps';
export { getAddressColumns } from './addressColumns';
export {
  ActionsMenuCellRenderer,
  getActionsColumn,
  withClickableCell,
  getReportViewActionsColumn,
  resolveGridRow,
  openAgGridContextMenu,
  isActionsColumn,
  actionMenuItem,
  createMenuPositionEvent,
  showRowActionsMenu,
  hideRowActionsMenu,
  RowActionsMenuHost,
} from './actionsMenu';



// WEBPACK FOOTER //
// ./src/components/grid/iLocalAGGrid.js